import { useState } from "preact/hooks";
import { simGateOpen, type FlowState } from "../flow/machine";
import type { InstallRunner } from "../flow/runner";
import { t } from "../i18n";
import { FALLBACK_MODELS, type SupportedModel } from "../models/supported";
import { BrowserCheckPanel, currentBrowserCheck } from "./BrowserCheck";
import { SimChecklist } from "./SimChecklist";

function yesNo(v: boolean | undefined): string {
  return v === undefined ? t("common.unknown") : v ? t("common.yes") : t("common.no");
}

export function DeviceCard({ s }: { s: FlowState }) {
  const d = s.device;
  if (!d) return null;
  return (
    <section aria-labelledby="device-h">
      <h3 id="device-h">{t("preflight.device_heading")}</h3>
      <dl class="kv">
        <dt>{t("preflight.device.product")}</dt><dd><code>{d.product || "?"}</code>{s.preflight?.modelName ? ` — ${s.preflight.modelName}` : ""}</dd>
        <dt>{t("preflight.device.serial")}</dt><dd><code>{d.serial}</code></dd>
        <dt>{t("preflight.device.unlocked")}</dt><dd>{yesNo(d.unlocked)}</dd>
        <dt>{t("preflight.device.unlock_ability")}</dt><dd>{yesNo(d.unlockAbility)}</dd>
        <dt>{t("preflight.device.slot")}</dt><dd>{d.currentSlot ?? t("common.unknown")}</dd>
        <dt>{t("preflight.device.anti_rollback")}</dt><dd>{d.antiRollback ?? t("common.unknown")}</dd>
        <dt>{t("preflight.device.bootloader")}</dt><dd><code>{d.versionBootloader ?? "?"}</code></dd>
        <dt>{t("preflight.device.baseband")}</dt><dd><code>{d.versionBaseband ?? "?"}</code></dd>
      </dl>
    </section>
  );
}

export function PreflightStep({ runner, s, models, reconnectOnly }: { runner: InstallRunner; s: FlowState; models: readonly SupportedModel[]; reconnectOnly: boolean }) {
  const [busy, setBusy] = useState(false);
  const check = currentBrowserCheck();
  const go = async () => {
    setBusy(true);
    if (reconnectOnly) await runner.reconnect();
    else await runner.connectAndPreflight();
    setBusy(false);
  };
  const p = s.preflight;
  const list = (models.length ? models : FALLBACK_MODELS).filter((m) => m.active).map((m) => `${m.name} (${m.codename})`).join(", ");
  return (
    <>
      <div class="panel">
        <h2 tabIndex={-1}>{t("preflight.heading")}</h2>
        {reconnectOnly && <p class="notice warn">{t("preflight.reconnect_hint")}</p>}
        {s.mode !== "stock" && <SimChecklist runner={runner} s={s} />}
        <ol class="steps">
          <li>{t("preflight.step1")}</li>
          <li>{t("preflight.step2")}</li>
          <li>{t("preflight.step3")}</li>
        </ol>
        <div class="row">
          <button type="button" class="primary" onClick={() => void go()} disabled={busy || !check.ok || !simGateOpen(s)}>
            {busy ? t("preflight.connecting") : reconnectOnly ? t("preflight.reconnect") : t("preflight.connect")}
          </button>
        </div>
        {p && !p.ok && (
          <div class="notice danger" role="alert">
            {p.code === "unsupported_model" && t("preflight.refused.unsupported_model", { product: s.device?.product ?? "?", models: list })}
            {p.code === "model_inactive" && t("preflight.refused.model_inactive", { model: p.modelName ?? s.device?.product ?? "?" })}
            {p.code === "us_carrier_variant" && t("preflight.refused.us_carrier_variant", { carrier: p.carrierHint ?? "?" })}
            {p.code !== "unsupported_model" && p.code !== "model_inactive" && p.code !== "us_carrier_variant" && t(`preflight.refused.${p.code}`)}
          </div>
        )}
        {p?.ok && (
          <div class="notice ok" role="status">
            <p>{t("preflight.ok", { model: p.modelName ?? s.model ?? "" })}</p>
            {p.warnings.includes("us_carrier_variant") && <p>{t("preflight.warn.us_carrier_variant", { carrier: p.carrierHint ?? "?" })}</p>}
            {s.device?.unlockAbility === undefined && !s.device?.unlocked && <p>{t("preflight.warn.unlock_ability_unknown")}</p>}
            {s.device?.unlocked && <p>{t("preflight.already_unlocked")}</p>}
          </div>
        )}
        <DeviceCard s={s} />
      </div>
      <div class="panel">
        <BrowserCheckPanel check={check} />
      </div>
    </>
  );
}
